import { useMemo } from "react";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useDepartmentNurseTypes } from "@/hooks/useDepartmentNurseTypes";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import type { EmploymentType, Nurse, NurseFullRole, WeekData } from "@/utils/models";
import { inverseRolesMap } from "@/utils/utilFunctions";
import { useAddStaffCandidates } from "./useAddStaffCandidates";
import { useAddStaffCostPredictions } from "./useAddStaffCostPredictions";
import { useAddStaffRows } from "./useAddStaffRows";
import type { AddStaffScheduleMode, AddStaffShift } from "./useAddStaffShift";

type Args = {
	shift: AddStaffShift;
	/** The panel's schedule weeks; `weekIndex` and `dayNo` are positions in them. */
	weeks: WeekData[];
	weekIndex: number;
	dayNo: number;
	rolesToDisplay: NurseFullRole[];
	requestJobId: string | undefined;
	contractTypes: EmploymentType[];
	/** An `isCharged` caller: only staff who can run the shift as charge. */
	chargeOnly: boolean;
	showActual: boolean;
	nurseIdsToIgnore: number[] | undefined;
};

/**
 * Roles the list may show. Role mode: the one role picked in step 1. Job mode: the caller's roles — and when a
 * job-managed caller scopes by job alone (a job section's gap passes no roles), every department role, since
 * the job filter is then the whole scope; an empty list would otherwise hide everyone.
 */
const useAllowedRoles = (mode: AddStaffScheduleMode, selectedRole: NurseFullRole | undefined, rolesToDisplay: NurseFullRole[]) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	return useMemo(() => {
		if (mode === "role") return selectedRole ? [selectedRole] : rolesToDisplay;
		return rolesToDisplay.length ? rolesToDisplay : departmentNurseTypes;
	}, [mode, selectedRole, rolesToDisplay, departmentNurseTypes]);
};

/** What the list is scoped to, named for the "No one here has the RN job yet" empty state. */
export type AddStaffListScope = { kind: "job" | "role"; name: string };

/** The job the list is scoped to (by display name — it may no longer be enabled), else role mode's one role. */
const useListScope = (shift: AddStaffShift, targetJobId: string | undefined): AddStaffListScope | null => {
	const { chosenDepartment } = useChosenDepartment();
	return useMemo(() => {
		if (targetJobId) {
			const job = chosenDepartment.jobs?.find((candidate) => candidate.id === targetJobId);
			return job ? { kind: "job", name: job.displayName } : null;
		}
		if (shift.mode === "role" && shift.selectedRole) return { kind: "role", name: inverseRolesMap[shift.selectedRole] };
		return null;
	}, [chosenDepartment.jobs, targetJobId, shift.mode, shift.selectedRole]);
};

/** No one, until the roster arrives. */
const NO_NURSES: Nurse[] = [];

/** Step 2's data: who is offered for the picked shift, their predicted cost, and each one's grid row. */
export const useAddStaffList = ({
	shift,
	weeks,
	weekIndex,
	dayNo,
	rolesToDisplay,
	requestJobId,
	contractTypes,
	chargeOnly,
	showActual,
	nurseIdsToIgnore,
}: Args) => {
	const { chosenDepartment } = useChosenDepartment();
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	// DEFAULT: no candidates until the roster arrives; the panel's weeks wait for it with the schedule cache on, and
	// the provider has loaded it with the cache off.
	// The job the shift is booked under: the step-1 job, or (role mode) whatever the request asks for.
	const targetJobId = shift.mode === "job" ? shift.selectedJobId : requestJobId;
	// Only job mode lists by job. A role department's request still carries a resolved job (or "unknown"), which
	// backend pickup ignores there (`notifications_agent/processor.py`), so role mode lists by role alone.
	const listJobId = shift.mode === "job" ? shift.selectedJobId : undefined;
	const allowedRoles = useAllowedRoles(shift.mode, shift.selectedRole, rolesToDisplay);
	const nurses = roster ?? NO_NURSES;
	const { candidates, nurseOccurrences, hasAnyHolder } = useAddStaffCandidates({
		weeks,
		nurses,
		weekIndex,
		dayNo,
		shiftNo: shift.shiftNo,
		allowedRoles,
		jobId: listJobId,
		contractTypes,
		chargeOnly,
		showActual,
		nurseIdsToIgnore,
	});
	const { predictions, isLoading, isAwaitingWindow } = useAddStaffCostPredictions(shift.window, targetJobId, nurses);
	const { rows, dateRange, availableCount } = useAddStaffRows({
		candidates,
		window: shift.window,
		weeks,
		weekIndex,
		dayNo,
		shiftNo: shift.shiftNo,
		staffingDate: shift.staffingDate,
		predictions,
		nurseOccurrences,
		showActual,
	});
	const scope = useListScope(shift, listJobId);
	return {
		targetJobId,
		rows,
		dateRange,
		availableCount,
		isLoadingPredictions: isLoading,
		/** The rows' cost / overtime aren't this window's yet — the add must wait for them. */
		arePredictionsPending: isAwaitingWindow,
		scope,
		hasAnyHolder,
	};
};
